import { useState } from 'react';
import { deleteWeight, updateWeight, type WeightEntry } from '../services/weightApi';

interface WeightListProps {
  entries: WeightEntry[];
  onChanged: () => void;
}

const PAGE_SIZE = 10;

function formatDisplay(isoString: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function toDatetimeLocal(isoString: string): string {
  const d = new Date(isoString);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

interface EditState {
  recordedAt: string;
  weight: string;
  date: string;
  saving: boolean;
  error: string | null;
}

export function WeightList({ entries, onChanged }: WeightListProps) {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<EditState | null>(null);
  const [deletingAt, setDeletingAt] = useState<string | null>(null);

  const sorted = [...entries].sort(
    (a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
  );

  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  const pageEntries = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  function startEdit(entry: WeightEntry) {
    setEditing({
      recordedAt: entry.recordedAt,
      weight: String(entry.weightLbs),
      date: toDatetimeLocal(entry.recordedAt),
      saving: false,
      error: null,
    });
  }

  function cancelEdit() {
    setEditing(null);
  }

  async function saveEdit() {
    if (!editing) return;
    const lbs = parseFloat(editing.weight);
    if (!editing.weight || isNaN(lbs) || lbs <= 0) {
      setEditing((e) => e && { ...e, error: 'Enter a valid weight.' });
      return;
    }
    setEditing((e) => e && { ...e, saving: true, error: null });
    try {
      await updateWeight(editing.recordedAt, lbs, new Date(editing.date).toISOString());
      setEditing(null);
      onChanged();
    } catch (err) {
      setEditing((e) => e && { ...e, saving: false, error: err instanceof Error ? err.message : 'Save failed.' });
    }
  }

  async function handleDelete(recordedAt: string) {
    setDeletingAt(recordedAt);
    try {
      await deleteWeight(recordedAt);
      // If deleted entry was on current page and it was the only one, go back
      if (pageEntries.length === 1 && page > 0) setPage((p) => p - 1);
      onChanged();
    } catch {
      // silently reset — the list will remain unchanged
    } finally {
      setDeletingAt(null);
    }
  }

  if (entries.length === 0) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <button
        onClick={() => { setOpen((o) => !o); setPage(0); }}
        className="w-full flex items-center justify-between px-6 py-4 text-sm font-medium text-gray-700 hover:bg-gray-50 transition focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
        aria-expanded={open}
      >
        <span>All data <span className="ml-1 text-gray-400 font-normal">({entries.length} {entries.length === 1 ? 'entry' : 'entries'})</span></span>
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="border-t border-gray-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                <th className="px-6 py-3">Date &amp; Time</th>
                <th className="px-6 py-3">Weight (lbs)</th>
                <th className="px-6 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pageEntries.map((entry) => {
                const isEditing = editing?.recordedAt === entry.recordedAt;
                const isDeleting = deletingAt === entry.recordedAt;

                if (isEditing && editing) {
                  return (
                    <tr key={entry.recordedAt} className="bg-indigo-50/50">
                      <td className="px-4 py-2">
                        <input
                          type="datetime-local"
                          value={editing.date}
                          onChange={(e) => setEditing((s) => s && { ...s, date: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </td>
                      <td className="px-4 py-2">
                        <input
                          type="number"
                          step="0.1"
                          min="1"
                          max="1500"
                          value={editing.weight}
                          onChange={(e) => setEditing((s) => s && { ...s, weight: e.target.value })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        {editing.error && (
                          <p className="text-red-500 text-xs mt-1">{editing.error}</p>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={saveEdit}
                            disabled={editing.saving}
                            className="px-3 py-1 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition"
                          >
                            {editing.saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={cancelEdit}
                            disabled={editing.saving}
                            className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={entry.recordedAt} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 text-gray-700">{formatDisplay(entry.recordedAt)}</td>
                    <td className="px-6 py-3 font-medium text-gray-900">{entry.weightLbs} lbs</td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => startEdit(entry)}
                          disabled={isDeleting || !!editing}
                          className="px-3 py-1 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-md hover:bg-indigo-50 disabled:opacity-40 transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(entry.recordedAt)}
                          disabled={isDeleting || !!editing}
                          className="px-3 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-md hover:bg-red-50 disabled:opacity-40 transition"
                        >
                          {isDeleting ? '…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100 bg-gray-50">
              <span className="text-xs text-gray-500">
                Page {page + 1} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 0}
                  className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-white disabled:opacity-40 transition"
                >
                  ← Prev
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1 text-xs font-medium text-gray-600 border border-gray-300 rounded-md hover:bg-white disabled:opacity-40 transition"
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
